<template>
  <main class="cover-auth">
    <div class="widget-auth">
      <a href="#">
        <div class="text-center mb-8">
          <img src="/public/images/logo-wide.svg" class="auth-logo" />
        </div>
      </a>

      <h2 class="font-semibold text-center my-6">Entrez le code de connexion</h2>
      <p class="text-read text-grey-600 text-center pb-5">
        Un code de connexion a été envoyé à votre adresse e-mail, sur WhatsApp, et par SMS.
      </p>

      <form class="form" @submit.prevent="handleSubmit">
        <div class="flex gap-4 mb-7">
          <input 
            v-for="(digit, index) in otpCode" 
            :key="index"
            type="text" 
            class="form-control-code" 
            :id="`otp-${index}`"
            v-model="otpCode[index]"
            maxlength="1"
            @input="handleInput(index, $event)"
            @keydown="handleKeydown(index, $event)"
            @paste="handlePaste"
            placeholder=""
            inputmode="numeric"
            pattern="[0-9]"
          >
        </div>
        
        <button 
          type="submit" 
          class="btn lg w-100 flex-center mt-5 mb-7"
          :disabled="!isCodeComplete"
        >
          <img src="/public/icons/icon-menu-securite.svg" class="btn-icon" alt="" />
          <span>Valider</span>
        </button>
        
        <a @click.prevent="resendOtp" class="cursor-pointer">
          <h5 class="text-primary-color text-center">
            {{ isResending ? 'Envoi en cours...' : 'Vous n\'avez pas reçu votre code ?' }}
          </h5>
        </a>
      </form>
    </div>
  </main>
</template>

<script lang="ts">
import { defineComponent, reactive, computed, nextTick, ref } from 'vue'
import { useRouter } from 'vue-router'
import authService from '@/services/authService'
import { showAlert } from '@/utils/swal'

export default defineComponent({
  name: 'SignInOtp',
  setup() {
    const router = useRouter()
    const otpCode = reactive<string[]>(['', '', '', '', '', ''])
    const isResending = ref(false)
    const username = ref('')

    // Récupérer l'identifiant stocké depuis la page de connexion
    const storedUsername = sessionStorage.getItem('loginUsername')
    if (storedUsername) {
      username.value = storedUsername
    } else {
      // Si pas d'identifiant, rediriger vers la page de connexion
      router.push('/auth/sign-in')
      return
    }

    const isCodeComplete = computed(() => {
      return otpCode.every(digit => digit !== '' && /^\d$/.test(digit))
    })

    const focusInput = (index: number) => {
      nextTick(() => {
        const input = document.getElementById(`otp-${index}`) as HTMLInputElement
        if (input) {
          input.focus()
        }
      })
    }

    const handleInput = (index: number, event: Event) => {
      const target = event.target as HTMLInputElement
      const value = target.value

      // Ne garder que les chiffres
      if (!/^\d*$/.test(value)) {
        otpCode[index] = ''
        return
      }

      // Si on entre un chiffre, passer au champ suivant
      if (value.length === 1 && index < 5) {
        focusInput(index + 1)
      }
    }

    const handleKeydown = (index: number, event: KeyboardEvent) => {
      // Gérer la touche Backspace
      if (event.key === 'Backspace' && otpCode[index] === '' && index > 0) {
        event.preventDefault()
        focusInput(index - 1)
      }

      // Gérer les flèches gauche/droite
      if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault()
        focusInput(index - 1)
      }

      if (event.key === 'ArrowRight' && index < 5) {
        event.preventDefault()
        focusInput(index + 1)
      }
    }

    const handlePaste = (event: ClipboardEvent) => {
      event.preventDefault()
      const pastedData = event.clipboardData?.getData('text')
      
      if (pastedData && /^\d{6}$/.test(pastedData)) {
        for (let i = 0; i < 6; i++) {
          otpCode[i] = pastedData[i]
        }
        focusInput(5)
      }
    }

    const handleSubmit = async () => {
      if (!isCodeComplete.value) {
        await showAlert.warning('Veuillez saisir le code OTP à 6 chiffres')
        return
      }

      const code = otpCode.join('')
      
      try {
        // Vérifier l'OTP pour finaliser la connexion
        const response = await authService.verifyOtp({
          identifiant: username.value,
          otp: code
        })
        
        if (response.status === 0) {
          // Connexion réussie, rediriger vers la page d'accueil
          await showAlert.success('Connexion réussie !')
          
          // Nettoyer les données de session
          sessionStorage.removeItem('loginUsername')
          
          const redirect = router.currentRoute.value.query.redirect as string | undefined
          const target = redirect || '/front'
          await router.push(target)
        } else {
          await showAlert.error(response.message || 'Code de sécurité incorrect. Veuillez réessayer.')
        }
      } catch (error) {
        console.error('Erreur lors de la connexion:', error)
        await showAlert.error('Une erreur est survenue. Veuillez réessayer.')
      }
    }

    const resendOtp = async () => {
      if (isResending.value) return

      if (!username.value) {
        await showAlert.warning('Session expirée. Veuillez vous reconnecter.')
        router.push('/auth/sign-in')
        return
      }

      isResending.value = true

      try {
        // Appel API pour renvoyer le code OTP
        const response = await authService.sendLoginOtp(username.value)
        
        if (response.status === 0) {
          await showAlert.success('Un nouveau code a été envoyé à votre adresse email !')
          
          // Réinitialiser les champs
          for (let i = 0; i < 6; i++) {
            otpCode[i] = ''
          }
          focusInput(0)
        } else {
          await showAlert.error(response.message || 'Une erreur est survenue.')
        }
      } catch (error) {
        console.error('Erreur lors du renvoi:', error)
        await showAlert.error('Une erreur est survenue. Veuillez réessayer.')
      } finally {
        isResending.value = false
      }
    }

    return {
      otpCode,
      isCodeComplete,
      isResending,
      handleInput,
      handleKeydown,
      handlePaste,
      handleSubmit,
      resendOtp
    }
  }
})
</script>

<style scoped>
.auth-logo {
  max-width: 200px;
  height: auto;
}

.form-control-code {
  width: 55px;
  height: 60px;
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.form-control-code:focus {
  outline: none;
  border-color: var(--primary-color, #007bff);
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.form-control-code:not(:placeholder-shown) {
  border-color: var(--primary-color, #007bff);
  background-color: #f8f9fa;
}

.flex {
  display: flex;
  justify-content: center;
}

.gap-4 {
  gap: 0.75rem;
}

.cursor-pointer {
  cursor: pointer;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .form-control-code {
    width: 45px;
    height: 50px;
    font-size: 20px;
  }
  
  .gap-4 {
    gap: 0.5rem;
  }
}
</style>

